import { getSalesId, getSalesValue } from '../hooks/useSalesApiResource';

const asNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const asNullableNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const asText = (value) => String(value ?? '').trim();

export const FIELD_TYPES = ['Dropdown', 'Whole Number', 'Decimal Number', 'Text', 'Toggle', 'Calculated', 'Hyperlink', 'Button'];
export const isWholeNumberFieldType = (fieldType) => asText(fieldType).toLowerCase() === 'whole number';
export const isDecimalNumberFieldType = (fieldType) => asText(fieldType).toLowerCase() === 'decimal number';
export const isNumericFieldType = (fieldType) => (
  isWholeNumberFieldType(fieldType) || isDecimalNumberFieldType(fieldType)
);
export const HYPERLINK_FIELD_TYPE = 'Hyperlink';
export const isHyperlinkFieldType = (fieldType) => asText(fieldType).toLowerCase() === HYPERLINK_FIELD_TYPE.toLowerCase();
export const BUTTON_FIELD_TYPE = 'Button';
export const isButtonFieldType = (fieldType) => asText(fieldType).toLowerCase() === BUTTON_FIELD_TYPE.toLowerCase();

export const getButtonMaxClicks = (value = {}) => {
  const maxRows = Number(value?.maxRows ?? value?.maxClicks);
  return Number.isInteger(maxRows) && maxRows > 0 ? maxRows : 0;
};

export const normalizeHyperlinkHref = (value) => {
  const trimmed = asText(value);
  if (!trimmed) return '';
  if (trimmed.startsWith('/')) return trimmed.startsWith('//') ? '' : trimmed;

  try {
    const url = new URL(trimmed);
    return ['http:', 'https:', 'mailto:', 'tel:'].includes(url.protocol) ? url.href : '';
  } catch {
    return '';
  }
};

export const normalizeMasterStation = (row = {}) => ({
  id: asNumber(getSalesId(row)),
  stationName: asText(getSalesValue(row, 'stationName', 'StationName')),
  sortOrder: asNumber(getSalesValue(row, 'sortOrder', 'SortOrder')),
});

export const normalizeConfigSection = (row = {}) => ({
  id: asNumber(getSalesId(row)),
  stationId: asNullableNumber(getSalesValue(row, 'stationId', 'StationId')),
  page: asText(getSalesValue(row, 'page', 'Page')),
  sectionKey: asText(getSalesValue(row, 'sectionKey', 'SectionKey')),
  label: asText(getSalesValue(row, 'label', 'Label')),
  sortOrder: asNumber(getSalesValue(row, 'sortOrder', 'SortOrder')),
});

export const isCostSummarySection = (section = {}) => [section.label, section.sectionKey]
  .some((value) => asText(value).toLowerCase() === 'cost summary');

export const normalizeConfigSubSection = (row = {}) => ({
  id: asNumber(getSalesId(row)),
  sectionId: asNumber(getSalesValue(row, 'sectionId', 'SectionId')),
  subSectionName: asText(getSalesValue(row, 'subSectionName', 'SubSectionName')),
  sortOrder: asNumber(getSalesValue(row, 'sortOrder', 'SortOrder')),
});

export const normalizeConfigField = (row = {}) => ({
  id: asNumber(getSalesId(row)),
  subSectionId: asNumber(getSalesValue(row, 'subSectionId', 'SubSectionId')),
  fieldName: asText(getSalesValue(row, 'fieldName', 'FieldName')),
  fieldType: asText(getSalesValue(row, 'fieldType', 'FieldType')),
  scope: asText(getSalesValue(row, 'scope', 'Scope')),
  sortOrder: asNumber(getSalesValue(row, 'sortOrder', 'SortOrder')),
});

export const normalizeConfigFieldValue = (row = {}) => ({
  id: asNumber(getSalesId(row)),
  configFieldId: asNumber(getSalesValue(row, 'configFieldId', 'ConfigFieldId')),
  optionValue: asText(getSalesValue(row, 'optionValue', 'OptionValue')),
  isDefaultValue: Boolean(getSalesValue(row, 'isDefaultValue', 'IsDefaultValue')),
  minValue: asNullableNumber(getSalesValue(row, 'minValue', 'MinValue')),
  maxValue: asNullableNumber(getSalesValue(row, 'maxValue', 'MaxValue')),
  // MaxRows is the current API contract. Keep MaxClicks as a read fallback so
  // older cached/test payloads remain compatible while the data migrates.
  maxClicks: asNullableNumber(getSalesValue(row, 'maxRows', 'MaxRows', 'maxClicks', 'MaxClicks')),
});

export const getFieldValueMode = (value = {}) => (
  value?.minValue !== null && value?.minValue !== undefined
    ? 'range'
    : value?.maxValue !== null && value?.maxValue !== undefined
      ? 'range'
      : 'fixed'
);

export const getFieldValueLabel = (value = {}) => {
  if (!value || typeof value !== 'object') return '';
  const maxClicks = getButtonMaxClicks(value);
  if (maxClicks) return `Maximum rows: ${maxClicks}`;
  const mode = getFieldValueMode(value);
  if (mode === 'range') {
    if (value.minValue !== null && value.minValue !== undefined && value.maxValue !== null && value.maxValue !== undefined) {
      return `${value.minValue}–${value.maxValue}`;
    }
    if (value.minValue !== null && value.minValue !== undefined) return `${value.minValue} or greater`;
    if (value.maxValue !== null && value.maxValue !== undefined) return `${value.maxValue} or less`;
  }
  return asText(value.optionValue) || `Value ${asNumber(value.id)}`;
};

const PUMP_TABLE_HP_COLORS = new Map([
  [0.5, '#fde2e4'],
  [1, '#fad2e1'],
  [1.5, '#e9d5ff'],
  [2, '#fff2bf'],
  [3, '#c9e8f7'],
  [5, '#c8efcf'],
  [7.5, '#fde0d0'],
  [10, '#dfe7fd'],
  [15, '#c7ceea'],
  [20, '#f1c0e8'],
  [25, '#cfbaf0'],
  [30, '#a3c4f3'],
  [40, '#90dbf4'],
  [50, '#8eecf5'],
  [60, '#98f5e1'],
  [75, '#e2f0cb'],
  [100, '#f6eac2'],
  [125, '#ffdac1'],
]);

export const getPumpTableHpColor = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '';
  if (PUMP_TABLE_HP_COLORS.has(numericValue)) return PUMP_TABLE_HP_COLORS.get(numericValue);
  const hue = ((Math.round(numericValue * 10) * 137.508) % 360 + 360) % 360;
  return `hsl(${hue.toFixed(1)} 70% 88%)`;
};

export const normalizeConfigValueRule = (row = {}) => ({
  id: asNumber(getSalesId(row)),
  fieldValueId: asNumber(getSalesValue(row, 'fieldValueId', 'FieldValueId')),
  ruleGroupId: asNumber(getSalesValue(row, 'ruleGroupId', 'RuleGroupId')),
  parentStationId: asNullableNumber(getSalesValue(row, 'parentStationId', 'ParentStationId')),
  parentFieldValueId: asNullableNumber(getSalesValue(row, 'parentFieldValueId', 'ParentFieldValueId')),
  isDisabled: Boolean(getSalesValue(row, 'isDisabled', 'IsDisabled')),
  isDefaultValue: Boolean(getSalesValue(row, 'isDefaultValue', 'IsDefaultValue')),
});

export const normalizers = {
  stations: (rows) => rows.map(normalizeMasterStation),
  sections: (rows) => rows.map(normalizeConfigSection),
  subSections: (rows) => rows.map(normalizeConfigSubSection),
  fields: (rows) => rows.map(normalizeConfigField),
  fieldValues: (rows) => rows.map(normalizeConfigFieldValue),
  valueRules: (rows) => rows.map(normalizeConfigValueRule),
};

const normalizeCoverageField = (row = {}) => ({
  fieldId: asNumber(getSalesValue(row, 'fieldId', 'FieldId')),
  valueCount: asNumber(getSalesValue(row, 'valueCount', 'ValueCount')),
  coveredValueCount: asNumber(getSalesValue(row, 'coveredValueCount', 'CoveredValueCount')),
  ruleCount: asNumber(getSalesValue(row, 'ruleCount', 'RuleCount')),
  isActive: Boolean(getSalesValue(row, 'isActive', 'IsActive')),
});

const normalizeCoverageSubSection = (row = {}) => ({
  subSectionId: asNumber(getSalesValue(row, 'subSectionId', 'SubSectionId')),
  fieldCount: asNumber(getSalesValue(row, 'fieldCount', 'FieldCount')),
  activeFieldCount: asNumber(getSalesValue(row, 'activeFieldCount', 'ActiveFieldCount')),
  valueCount: asNumber(getSalesValue(row, 'valueCount', 'ValueCount')),
  coveredValueCount: asNumber(getSalesValue(row, 'coveredValueCount', 'CoveredValueCount')),
  ruleCount: asNumber(getSalesValue(row, 'ruleCount', 'RuleCount')),
});

const normalizeCoverageSection = (row = {}) => ({
  sectionId: asNumber(getSalesValue(row, 'sectionId', 'SectionId')),
  subSectionCount: asNumber(getSalesValue(row, 'subSectionCount', 'SubSectionCount')),
  activeSubSectionCount: asNumber(getSalesValue(row, 'activeSubSectionCount', 'ActiveSubSectionCount')),
  fieldCount: asNumber(getSalesValue(row, 'fieldCount', 'FieldCount')),
  activeFieldCount: asNumber(getSalesValue(row, 'activeFieldCount', 'ActiveFieldCount')),
  valueCount: asNumber(getSalesValue(row, 'valueCount', 'ValueCount')),
  coveredValueCount: asNumber(getSalesValue(row, 'coveredValueCount', 'CoveredValueCount')),
  ruleCount: asNumber(getSalesValue(row, 'ruleCount', 'RuleCount')),
});

const getCoveragePayload = (response = {}) => {
  if (!response || typeof response !== 'object') return {};
  return response.data || response.Data || response.result || response.Result || response;
};

export const normalizeConfigurationCoverage = (response, stationId) => {
  const payload = getCoveragePayload(response);
  const sections = (Array.isArray(getSalesValue(payload, 'sections', 'Sections')) ? getSalesValue(payload, 'sections', 'Sections') : []).map(normalizeCoverageSection);
  const subSections = (Array.isArray(getSalesValue(payload, 'subSections', 'SubSections', 'subsections', 'Subsections')) ? getSalesValue(payload, 'subSections', 'SubSections', 'subsections', 'Subsections') : []).map(normalizeCoverageSubSection);
  const fields = (Array.isArray(getSalesValue(payload, 'fields', 'Fields')) ? getSalesValue(payload, 'fields', 'Fields') : []).map(normalizeCoverageField);
  const totals = getSalesValue(payload, 'totals', 'Totals', 'summary', 'Summary') || {};
  const total = (camelKey, pascalKey, fallback) => {
    const value = getSalesValue(totals, camelKey, pascalKey);
    return value === undefined ? fallback : asNumber(value);
  };

  return {
    stationId: asNumber(getSalesValue(payload, 'parentStationId', 'ParentStationId', 'stationId', 'StationId'), asNumber(stationId)),
    sections,
    subSections,
    fields,
    totals: {
      sectionCount: total('sectionCount', 'SectionCount', sections.length),
      activeSectionCount: total('activeSectionCount', 'ActiveSectionCount', sections.filter((row) => row.activeFieldCount > 0).length),
      subSectionCount: total('subSectionCount', 'SubSectionCount', subSections.length),
      activeSubSectionCount: total('activeSubSectionCount', 'ActiveSubSectionCount', subSections.filter((row) => row.activeFieldCount > 0).length),
      fieldCount: total('fieldCount', 'FieldCount', fields.length),
      activeFieldCount: total('activeFieldCount', 'ActiveFieldCount', fields.filter((row) => row.isActive).length),
      valueCount: total('valueCount', 'ValueCount', sumCoverage(fields, 'valueCount')),
      coveredValueCount: total('coveredValueCount', 'CoveredValueCount', sumCoverage(fields, 'coveredValueCount')),
      ruleCount: total('ruleCount', 'RuleCount', sumCoverage(fields, 'ruleCount')),
    },
  };
};

const sumCoverage = (rows, key) => rows.reduce((total, row) => total + asNumber(row[key]), 0);

export const buildConfigurationCoverage = ({ sections = [], subSections = [], fields = [], fieldValues = [], valueRules = [] }, stationId) => {
  const numericStationId = asNumber(stationId);
  const availableSections = getSectionsForStation(sections, numericStationId);
  const sectionIds = new Set(availableSections.map((section) => section.id));
  const availableSubSections = subSections.filter((subSection) => sectionIds.has(subSection.sectionId));
  const subSectionIds = new Set(availableSubSections.map((subSection) => subSection.id));
  const availableFields = fields.filter((field) => subSectionIds.has(field.subSectionId));
  const fieldIds = new Set(availableFields.map((field) => field.id));
  const availableValues = fieldValues.filter((value) => fieldIds.has(value.configFieldId));
  const valueById = new Map(availableValues.map((value) => [value.id, value]));
  const stationRules = valueRules.filter((rule) => rule.parentStationId === numericStationId && valueById.has(rule.fieldValueId));

  const valuesByField = new Map();
  availableValues.forEach((value) => {
    const values = valuesByField.get(value.configFieldId) || [];
    values.push(value);
    valuesByField.set(value.configFieldId, values);
  });

  const rulesByField = new Map();
  stationRules.forEach((rule) => {
    const fieldId = valueById.get(rule.fieldValueId).configFieldId;
    const rules = rulesByField.get(fieldId) || [];
    rules.push(rule);
    rulesByField.set(fieldId, rules);
  });

  const fieldCoverage = availableFields.map((field) => {
    const values = valuesByField.get(field.id) || [];
    const rules = rulesByField.get(field.id) || [];
    const coveredValueIds = new Set(rules.map((rule) => rule.fieldValueId));
    return {
      fieldId: field.id,
      valueCount: values.length,
      coveredValueCount: coveredValueIds.size,
      ruleCount: rules.length,
      isActive: rules.length > 0,
    };
  });
  const fieldCoverageById = new Map(fieldCoverage.map((row) => [row.fieldId, row]));

  const subSectionCoverage = availableSubSections.map((subSection) => {
    const rows = availableFields
      .filter((field) => field.subSectionId === subSection.id)
      .map((field) => fieldCoverageById.get(field.id));
    return {
      subSectionId: subSection.id,
      fieldCount: rows.length,
      activeFieldCount: rows.filter((row) => row.isActive).length,
      valueCount: sumCoverage(rows, 'valueCount'),
      coveredValueCount: sumCoverage(rows, 'coveredValueCount'),
      ruleCount: sumCoverage(rows, 'ruleCount'),
    };
  });
  const subSectionCoverageById = new Map(subSectionCoverage.map((row) => [row.subSectionId, row]));

  const sectionCoverage = availableSections.map((section) => {
    const rows = availableSubSections
      .filter((subSection) => subSection.sectionId === section.id)
      .map((subSection) => subSectionCoverageById.get(subSection.id));
    return {
      sectionId: section.id,
      subSectionCount: rows.length,
      activeSubSectionCount: rows.filter((row) => row.activeFieldCount > 0).length,
      fieldCount: sumCoverage(rows, 'fieldCount'),
      activeFieldCount: sumCoverage(rows, 'activeFieldCount'),
      valueCount: sumCoverage(rows, 'valueCount'),
      coveredValueCount: sumCoverage(rows, 'coveredValueCount'),
      ruleCount: sumCoverage(rows, 'ruleCount'),
    };
  });

  return {
    stationId: numericStationId,
    sections: sectionCoverage,
    subSections: subSectionCoverage,
    fields: fieldCoverage,
    totals: {
      sectionCount: sectionCoverage.length,
      activeSectionCount: sectionCoverage.filter((row) => row.activeFieldCount > 0).length,
      subSectionCount: subSectionCoverage.length,
      activeSubSectionCount: subSectionCoverage.filter((row) => row.activeFieldCount > 0).length,
      fieldCount: fieldCoverage.length,
      activeFieldCount: fieldCoverage.filter((row) => row.isActive).length,
      valueCount: sumCoverage(fieldCoverage, 'valueCount'),
      coveredValueCount: sumCoverage(fieldCoverage, 'coveredValueCount'),
      ruleCount: sumCoverage(fieldCoverage, 'ruleCount'),
    },
  };
};

const getMutationResponseRow = (response) => {
  if (Array.isArray(response)) return response[0] || {};
  if (!response || typeof response !== 'object') return {};

  for (const key of ['data', 'Data', 'item', 'Item', 'result', 'Result']) {
    const value = response[key];
    if (Array.isArray(value)) return value[0] || {};
    if (value && typeof value === 'object') return value;
  }

  return response;
};

export const reconcileResourceMutation = (resource, rows, method, payload, response) => {
  const methodUpper = String(method || '').toUpperCase();
  const currentRows = Array.isArray(rows) ? rows : [];

  if (methodUpper === 'DELETE') {
    const deletedId = asNumber(getSalesId(payload));
    return currentRows.filter((row) => row.id !== deletedId);
  }

  const responseRow = getMutationResponseRow(response);
  const normalizedRecord = normalizers[resource]([{ ...responseRow, ...payload }])[0];

  // Identity-backed POST endpoints may return only a success message. The
  // caller should perform one targeted GET when the new ID is unavailable.
  if (!normalizedRecord || normalizedRecord.id <= 0) return null;

  return [
    ...currentRows.filter((row) => row.id !== normalizedRecord.id),
    normalizedRecord,
  ];
};

export const findMatchingValueRule = (rows, payload) => {
  const expected = normalizeConfigValueRule(payload);
  return (Array.isArray(rows) ? rows : []).find((rule) => (
    rule.fieldValueId === expected.fieldValueId &&
    rule.ruleGroupId === expected.ruleGroupId &&
    rule.parentStationId === expected.parentStationId &&
    rule.parentFieldValueId === expected.parentFieldValueId &&
    rule.isDisabled === expected.isDisabled &&
    rule.isDefaultValue === expected.isDefaultValue
  ));
};

export const sortByOrderThenName = (rows, nameKey) => [...rows].sort((left, right) => {
  const orderDelta = asNumber(left.sortOrder) - asNumber(right.sortOrder);
  if (orderDelta !== 0) return orderDelta;
  return asText(left[nameKey]).localeCompare(asText(right[nameKey]));
});

export const getPreviewSectionFields = (subSection = {}) => {
  const activeFields = subSection.fields || [];
  return sortByOrderThenName(
    [...activeFields, ...(subSection.disabledFields || [])],
    'fieldName'
  );
};

const BASE_STATION_GROUP_LABELS = new Map([
  ['sales multiplier', 'Sales Multiplier'],
  ['steel adjustment', 'Steel Adjustment'],
  ['outside commission', 'Outside Commission Total'],
  ['rebate', 'Rebates'],
  ['setup multiplier', 'Setup Multiplier'],
]);

const BASE_STATION_FIELD_LABELS = new Map([
  ['startup allowance', 'Start Up Allowance'],
]);

export const buildBaseStationPreviewLayout = (fields = []) => {
  const sortedFields = sortByOrderThenName(fields, 'fieldName');
  const amountFieldByBaseName = new Map(sortedFields.flatMap((field) => {
    const match = asText(field.fieldName).match(/^(.*)\s+amount$/i);
    return match ? [[match[1].trim().toLowerCase(), field]] : [];
  }));
  const groupedFieldIds = new Set();

  return sortedFields.flatMap((field) => {
    if (groupedFieldIds.has(field.id)) return [];
    const normalizedName = asText(field.fieldName).toLowerCase();
    // Setup Multiplier is stored without the "Percent" suffix by the API.
    const percentMatch = normalizedName === 'setup multiplier'
      ? [field.fieldName, 'setup multiplier']
      : asText(field.fieldName).match(/^(.*)\s+percent(?:age)?$/i);
    if (percentMatch) {
      const baseName = percentMatch[1].trim();
      const amountField = amountFieldByBaseName.get(baseName.toLowerCase());
      if (amountField) {
        groupedFieldIds.add(field.id);
        groupedFieldIds.add(amountField.id);
        return [{
          key: `pair:${field.id}:${amountField.id}`,
          type: 'pair',
          label: BASE_STATION_GROUP_LABELS.get(baseName.toLowerCase()) || baseName,
          percentageField: field,
          amountField,
        }];
      }
    }

    groupedFieldIds.add(field.id);
    return [{
      key: `field:${field.id}`,
      type: 'field',
      field,
      label: BASE_STATION_FIELD_LABELS.get(normalizedName) || field.fieldName,
      prefix: percentMatch ? undefined : '$',
      fullWidth: ['list price', 'station price', 'taxes'].includes(normalizedName),
    }];
  });
};

export const getSectionsForStation = (sections, stationId) => sortByOrderThenName(
  sections.filter((section) => section.stationId === null || section.stationId === Number(stationId)),
  'sectionKey'
);

export const getNextId = (rows) => Math.max(0, ...rows.map((row) => asNumber(row.id))) + 1;

export const getNextRuleGroupId = (rules) => Math.max(0, ...rules.map((rule) => asNumber(rule.ruleGroupId))) + 1;

export const getValueRuleGroups = (rules, stationId, fieldValueIds) => {
  const numericStationId = asNumber(stationId);
  const allowedValueIds = fieldValueIds instanceof Set ? fieldValueIds : new Set(fieldValueIds || []);
  const rows = Array.isArray(rules) ? rules : [];
  const groups = new Map();
  // Index once instead of scanning the entire rules table for every branch.
  const rowsByBranch = new Map();
  rows.forEach(rule => {
    if (!allowedValueIds.has(rule.fieldValueId)) return;
    if (rule.parentStationId !== numericStationId && !(rule.parentStationId === null && rule.parentFieldValueId !== null)) return;
    const key = `${rule.ruleGroupId}:${rule.fieldValueId}`;
    if (!rowsByBranch.has(key)) rowsByBranch.set(key, []);
    rowsByBranch.get(key).push(rule);
  });

  rows
    .filter((rule) => rule.parentStationId === numericStationId && allowedValueIds.has(rule.fieldValueId))
    .forEach((stationRule) => {
      const groupKey = String(stationRule.ruleGroupId);
      if (!groups.has(groupKey)) groups.set(groupKey, { key: groupKey, ruleGroupId: stationRule.ruleGroupId, values: [] });
      const group = groups.get(groupKey);
      if (group.values.some((value) => value.fieldValueId === stationRule.fieldValueId)) return;

      const valueRows = rowsByBranch.get(`${stationRule.ruleGroupId}:${stationRule.fieldValueId}`) || [];
      const stationRules = valueRows.filter((rule) => rule.parentStationId === numericStationId);
      const dependencies = valueRows.filter((rule) => rule.parentStationId === null && rule.parentFieldValueId !== null);
      group.values.push({
        key: `${stationRule.ruleGroupId}:${stationRule.fieldValueId}`,
        fieldValueId: stationRule.fieldValueId,
        stationRule: stationRules[0],
        stationRules,
        dependencies,
        rows: [...stationRules, ...dependencies],
        isDisabled: stationRules[0]?.isDisabled || false,
        isDefaultValue: stationRules[0]?.isDefaultValue || false,
      });
    });

  return [...groups.values()]
    .map((group) => {
      const values = [...group.values].sort((left, right) => left.fieldValueId - right.fieldValueId);
      return { ...group, values, rows: values.flatMap((value) => value.rows) };
    })
    .sort((left, right) => left.ruleGroupId - right.ruleGroupId);
};

export const filterValueRuleGroups = (
  groups = [],
  searchTerm = '',
  status = 'all',
  fieldValues = [],
  fields = [],
  dependencyFilters = {}
) => {
  const query = asText(searchTerm).trim().toLowerCase();
  const queryTerms = query.split(/\s+/).filter(Boolean);
  const valueById = new Map(fieldValues.map((value) => [value.id, value]));
  const fieldById = new Map(fields.map((field) => [field.id, field]));
  const selectedDependencyValueIds = Object.values(dependencyFilters)
    .map((valueId) => asNumber(valueId))
    .filter((valueId) => valueId > 0);
  const branchMatchesStatus = (branch) => {
    if (status === 'enabled') return !branch.isDisabled;
    if (status === 'disabled') return branch.isDisabled;
    if (status === 'default') return branch.isDefaultValue;
    if (status === 'dependencies') return branch.dependencies.length > 0;
    return true;
  };

  return groups.flatMap((group) => {
    const groupMatchesQuery = !query
      || `rule group ${group.ruleGroupId} ${group.ruleGroupId}`.includes(query);
    const values = group.values.filter((branch) => {
      if (!branchMatchesStatus(branch)) return false;
      const dependencyValueIds = new Set(
        branch.dependencies.map((dependency) => asNumber(dependency.parentFieldValueId))
      );
      if (!selectedDependencyValueIds.every((valueId) => dependencyValueIds.has(valueId))) return false;
      if (groupMatchesQuery) return true;

      const value = valueById.get(branch.fieldValueId);
      const dependencyText = branch.dependencies.map((dependency) => {
        const dependencyValue = valueById.get(dependency.parentFieldValueId);
        const dependencyField = fieldById.get(dependencyValue?.configFieldId);
        return [
          dependency.parentFieldValueId,
          dependencyValue ? getFieldValueLabel(dependencyValue) : '',
          dependencyField?.fieldName,
          dependencyField?.id,
        ].join(' ');
      }).join(' ');
      const searchText = [
        branch.fieldValueId,
        value ? getFieldValueLabel(value) : '',
        branch.isDisabled ? 'disabled' : 'enabled',
        branch.isDefaultValue ? 'default' : '',
        dependencyText,
      ].join(' ').toLowerCase();
      return queryTerms.every((term) => searchText.includes(term));
    });

    if (values.length === 0) return [];
    return [{
      ...group,
      values,
      rows: values.flatMap((value) => value.rows),
      totalValueCount: group.values.length,
    }];
  });
};

export const getValueRuleDependencyFilterOptions = (
  groups = [],
  fieldValues = [],
  fields = []
) => {
  const valueById = new Map(fieldValues.map((value) => [value.id, value]));
  const fieldById = new Map(fields.map((field) => [field.id, field]));
  const optionsByFieldId = new Map();

  groups.forEach((group) => {
    group.values.forEach((branch) => {
      branch.dependencies.forEach((dependency) => {
        const dependencyValue = valueById.get(asNumber(dependency.parentFieldValueId));
        const dependencyField = fieldById.get(dependencyValue?.configFieldId);
        if (!dependencyValue || !dependencyField) return;

        if (!optionsByFieldId.has(dependencyField.id)) {
          optionsByFieldId.set(dependencyField.id, {
            fieldId: dependencyField.id,
            fieldName: dependencyField.fieldName || `Field ${dependencyField.id}`,
            valuesById: new Map(),
          });
        }
        optionsByFieldId.get(dependencyField.id).valuesById.set(dependencyValue.id, dependencyValue);
      });
    });
  });

  return [...optionsByFieldId.values()]
    .map(({ valuesById, ...field }) => ({
      ...field,
      values: [...valuesById.values()].sort((left, right) => (
        getFieldValueLabel(left).localeCompare(
          getFieldValueLabel(right),
          undefined,
          { numeric: true, sensitivity: 'base' }
        )
      )),
    }))
    .sort((left, right) => left.fieldName.localeCompare(right.fieldName, undefined, { sensitivity: 'base' }));
};

const getSelectedValueIdSet = (selections = {}) => new Set(
  Object.values(selections)
    .map((value) => asNumber(value))
    .filter((value) => value > 0)
);

const areFieldDependenciesMet = (
  dependencies = [],
  fieldValues = [],
  selections = {},
  ignoredFieldIds = new Set(),
  context
) => {
  const selectedValueIds = context?.selectedValueIds || getSelectedValueIdSet(selections);
  const valueById = context?.valueById || new Map(fieldValues.map((value) => [value.id, value]));
  const dependenciesByField = new Map();

  dependencies.forEach((dependency) => {
    const parentValueId = asNumber(dependency.parentFieldValueId);
    const parentFieldId = valueById.get(parentValueId)?.configFieldId;
    if (parentFieldId && ignoredFieldIds.has(parentFieldId)) return;
    const fieldKey = parentFieldId || `unknown:${parentValueId}`;
    if (!dependenciesByField.has(fieldKey)) dependenciesByField.set(fieldKey, []);
    dependenciesByField.get(fieldKey).push(parentValueId);
  });

  return Array.from(dependenciesByField.values()).every((allowedParentValueIds) => (
    allowedParentValueIds.some((parentValueId) => selectedValueIds.has(parentValueId))
  ));
};

export const findMatchingFieldValue = (rows, payload) => {
  const expected = normalizeConfigFieldValue(payload);
  return (Array.isArray(rows) ? rows : []).find((value) => (
    value.configFieldId === expected.configFieldId
    && value.optionValue === expected.optionValue
    && value.isDefaultValue === expected.isDefaultValue
    && value.minValue === expected.minValue
    && value.maxValue === expected.maxValue
    && value.maxClicks === expected.maxClicks
  ));
};

const getPreferredPreviewValueId = (values = []) => {
  const ruleDefault = values.find((value) => value.isRuleDefault);
  if (ruleDefault) return ruleDefault.id;
  const generalDefault = values.find((value) => value.isDefaultValue);
  if (generalDefault) return generalDefault.id;
  const enabledValue = values.find((value) => !value.isDisabled);
  return (enabledValue || values[0])?.id || 0;
};

export const getPreviewAllowedFieldValues = ({ field, fieldValues = [], valueRules = [], stationId, selections = {} }) => {
  if (!field?.id) return [];
  const valuesForField = fieldValues
    .filter((value) => value.configFieldId === field.id)
    .sort((left, right) => getFieldValueLabel(left).localeCompare(getFieldValueLabel(right), undefined, { numeric: true }));
  if (valuesForField.length === 0) return [];

  const valueById = new Map(valuesForField.map((value) => [value.id, value]));
  const ruleGroups = getValueRuleGroups(valueRules, stationId, new Set(valueById.keys()));
  if (ruleGroups.length === 0) {
    if (asText(field.fieldType).toLowerCase() !== 'calculated') return [];
    return valuesForField.map((value) => ({
      ...value,
      label: getFieldValueLabel(value),
      isDisabled: false,
      isRuleDefault: false,
    }));
  }

  const allowedValues = new Map();
  const dependencyContext = {
    selectedValueIds: getSelectedValueIdSet(selections),
    valueById: new Map(fieldValues.map(value => [value.id, value])),
  };

  ruleGroups.forEach((group) => {
    group.values.forEach((branch) => {
      const dependenciesMet = areFieldDependenciesMet(branch.dependencies, fieldValues, selections, undefined, dependencyContext);
      if (!dependenciesMet) return;

      const value = valueById.get(branch.fieldValueId);
      if (!value) return;

      const existing = allowedValues.get(value.id);
      allowedValues.set(value.id, {
        ...value,
        label: getFieldValueLabel(value),
        isDisabled: existing ? existing.isDisabled && branch.isDisabled : branch.isDisabled,
        isRuleDefault: Boolean(existing?.isRuleDefault || branch.isDefaultValue),
        isDefaultValue: Boolean(value.isDefaultValue || branch.isDefaultValue),
      });
    });
  });

  return Array.from(allowedValues.values())
    .sort((left, right) => getFieldValueLabel(left).localeCompare(getFieldValueLabel(right), undefined, { numeric: true }));
};

const PERFORMANCE_FIELD_NAMES = {
  dischargePsi: 'discharge psi',
  gpm: 'gpm',
  motorHp: 'motor hp',
};

const PUMP_TYPE_FIELD_NAME = 'pump type';

const BOOSTER_FIELD_NAMES = {
  configuration: 'configuration',
  dynamicInletPsi: 'dynamic inlet psi',
};

const getPerformanceField = (fields, fieldName) => fields.find((field) => (
  asText(field.fieldName).toLowerCase() === fieldName
));

const uniqueValuesById = (values = []) => Array.from(
  new Map(values.filter(Boolean).map((value) => [value.id, value])).values()
).sort((left, right) => getFieldValueLabel(left).localeCompare(
  getFieldValueLabel(right),
  undefined,
  { numeric: true }
));

const valueContainsNumber = (value, input) => {
  if (input === '' || input === null || input === undefined) return false;
  const numericInput = Number(input);
  if (!Number.isFinite(numericInput)) return false;
  const min = value?.minValue;
  const max = value?.maxValue;
  const hasMin = min !== null && min !== undefined;
  const hasMax = max !== null && max !== undefined;
  if (hasMin && numericInput < Number(min)) return false;
  if (hasMax && numericInput > Number(max)) return false;
  if (hasMin || hasMax) return true;
  const fixedValue = Number(value?.optionValue);
  return Number.isFinite(fixedValue) && numericInput === fixedValue;
};

export const getAdjustedDischargePsi = ({ configurationValue, dischargePsi, dynamicInletPsi } = {}) => {
  const numericDischargePsi = Number(dischargePsi);
  if (dischargePsi === '' || dischargePsi === null || dischargePsi === undefined || !Number.isFinite(numericDischargePsi)) {
    return dischargePsi;
  }
  if (asText(getFieldValueLabel(configurationValue)).toLowerCase() !== 'booster') return numericDischargePsi;

  const numericDynamicInletPsi = Number(dynamicInletPsi);
  const inletAdjustment = dynamicInletPsi === ''
    || dynamicInletPsi === null
    || dynamicInletPsi === undefined
    || !Number.isFinite(numericDynamicInletPsi)
    ? 0
    : numericDynamicInletPsi;
  return numericDischargePsi - inletAdjustment;
};

const getBranchDependencyIds = (branch, fieldId, valueById) => (
  (branch.dependencies || [])
    .map((dependency) => valueById.get(asNumber(dependency.parentFieldValueId)))
    .filter((value) => value?.configFieldId === fieldId)
    .map((value) => value.id)
);

const getStationFieldBranches = (field, fieldValues, valueRules, stationId) => {
  const valueIds = new Set(fieldValues
    .filter((value) => value.configFieldId === field.id)
    .map((value) => value.id));
  return getValueRuleGroups(valueRules, stationId, valueIds)
    .flatMap((group) => group.values)
    .filter((branch) => !branch.isDisabled);
};

export const buildPreviewPerformanceConstraints = (sourceData = {}, stationId, selections = {}, options = {}) => {
  const fields = sourceData.fields || [];
  const fieldValues = sourceData.fieldValues || [];
  const valueRules = sourceData.valueRules || [];
  const dischargePsiField = getPerformanceField(fields, PERFORMANCE_FIELD_NAMES.dischargePsi);
  const gpmField = getPerformanceField(fields, PERFORMANCE_FIELD_NAMES.gpm);
  const motorHpField = getPerformanceField(fields, PERFORMANCE_FIELD_NAMES.motorHp);
  const configurationField = getPerformanceField(fields, BOOSTER_FIELD_NAMES.configuration);
  const dynamicInletPsiField = getPerformanceField(fields, BOOSTER_FIELD_NAMES.dynamicInletPsi);
  if (!dischargePsiField || !gpmField || !motorHpField) return null;

  const valueById = new Map(fieldValues.map((value) => [value.id, value]));
  const performanceFieldIds = new Set([dischargePsiField.id, gpmField.id, motorHpField.id]);
  const externalDependencyFieldIds = new Set(
    (options.externalDependencyFieldIds || []).map((fieldId) => asNumber(fieldId)).filter(Boolean)
  );
  const dependencyContext = { valueById, selectedValueIds: getSelectedValueIdSet(selections) };
  const getActiveBranches = (field) => getStationFieldBranches(field, fieldValues, valueRules, stationId)
    .filter((branch) => {
      if (options.ignoreExternalDependencies) return true;
      const dependencies = externalDependencyFieldIds.size > 0
        ? branch.dependencies.filter((dependency) => {
            const dependencyValue = valueById.get(asNumber(dependency.parentFieldValueId));
            return externalDependencyFieldIds.has(dependencyValue?.configFieldId);
          })
        : branch.dependencies;
      return areFieldDependenciesMet(
        dependencies,
        fieldValues,
        selections,
        performanceFieldIds,
        dependencyContext
      );
    });
  const psiBranches = getActiveBranches(dischargePsiField);
  const gpmBranches = getActiveBranches(gpmField);
  const hpBranches = getActiveBranches(motorHpField);
  if (gpmBranches.length === 0 || hpBranches.length === 0) return null;

  const dependencyPsiValues = uniqueValuesById([
    ...gpmBranches,
    ...hpBranches,
  ].flatMap((branch) => getBranchDependencyIds(branch, dischargePsiField.id, valueById)
    .map((valueId) => valueById.get(valueId))));
  const psiValues = psiBranches.length > 0
    ? uniqueValuesById(psiBranches.map((branch) => valueById.get(branch.fieldValueId)))
    : dependencyPsiValues;
  const gpmValues = uniqueValuesById(gpmBranches.map((branch) => valueById.get(branch.fieldValueId)));
  const hpValues = uniqueValuesById(hpBranches.map((branch) => valueById.get(branch.fieldValueId)));
  if (psiValues.length === 0 || gpmValues.length === 0 || hpValues.length === 0) return null;

  const branchesByValueId = (branches) => branches.reduce((result, branch) => {
    if (!result.has(branch.fieldValueId)) result.set(branch.fieldValueId, []);
    result.get(branch.fieldValueId).push(branch);
    return result;
  }, new Map());
  const psiBranchesByValueId = branchesByValueId(psiBranches);
  const gpmBranchesByValueId = branchesByValueId(gpmBranches);
  const hpBranchesByValueId = branchesByValueId(hpBranches);
  const tupleDependencyCache = new Map();
  const branchMatchesTuple = (branch, tuple) => {
    let compiled = tupleDependencyCache.get(branch);
    if (!compiled) {
      const dependenciesByField = new Map();
      (branch.dependencies || []).forEach((dependency) => {
        const dependencyValue = valueById.get(asNumber(dependency.parentFieldValueId));
        if (!dependencyValue || !performanceFieldIds.has(dependencyValue.configFieldId)) return;
        if (!dependenciesByField.has(dependencyValue.configFieldId)) {
          dependenciesByField.set(dependencyValue.configFieldId, []);
        }
        dependenciesByField.get(dependencyValue.configFieldId).push(dependencyValue.id);
      });
      compiled = Array.from(dependenciesByField.entries());
      tupleDependencyCache.set(branch, compiled);
    }
    return compiled.every(([fieldId, allowedValueIds]) => allowedValueIds.includes(
      fieldId === dischargePsiField.id ? tuple.psiValueId : fieldId === gpmField.id ? tuple.gpmValueId : tuple.hpValueId
    ));
  };
  const valueHasMatchingBranch = (branchesById, valueId, tuple, allowMissing = false) => {
    const branches = branchesById.get(valueId) || [];
    return (allowMissing && branches.length === 0) || branches.some((branch) => branchMatchesTuple(branch, tuple));
  };

  const tuplesByKey = new Map();
  psiValues.forEach((psiValue) => {
    gpmValues.forEach((gpmValue) => {
      hpValues.forEach((hpValue) => {
        const tuple = { psiValueId: psiValue.id, gpmValueId: gpmValue.id, hpValueId: hpValue.id };
        if (!valueHasMatchingBranch(psiBranchesByValueId, psiValue.id, tuple, psiBranches.length === 0)) return;
        if (!valueHasMatchingBranch(gpmBranchesByValueId, gpmValue.id, tuple)) return;
        if (!valueHasMatchingBranch(hpBranchesByValueId, hpValue.id, tuple)) return;
        if (options.includeRuleBranches) tuple.ruleBranches = [
          [motorHpField.id, hpBranchesByValueId, hpValue.id],
          [gpmField.id, gpmBranchesByValueId, gpmValue.id],
          [dischargePsiField.id, psiBranchesByValueId, psiValue.id],
        ].flatMap(([fieldId, branchesById, valueId]) => (branchesById.get(valueId) || [])
          .filter((branch) => branchMatchesTuple(branch, tuple))
          .map((branch) => ({ fieldId, key: branch.key })));
        tuplesByKey.set(`${tuple.psiValueId}:${tuple.gpmValueId}:${tuple.hpValueId}`, tuple);
      });
    });
  });
  const tuples = Array.from(tuplesByKey.values());
  if (tuples.length === 0) return null;

  const minimumForValues = (values) => Math.min(...values.map((value) => {
    if (value.minValue !== null && value.minValue !== undefined) return Number(value.minValue);
    const fixedValue = Number(value.optionValue);
    return Number.isFinite(fixedValue) ? fixedValue : Number.POSITIVE_INFINITY;
  }));
  const selectedConfigurationValue = fieldValues.find((value) => (
    value.configFieldId === configurationField?.id
    && value.id === asNumber(selections[configurationField?.id])
  ));
  const selectedDynamicInletValue = fieldValues.find((value) => (
    value.configFieldId === dynamicInletPsiField?.id
    && value.id === asNumber(selections[dynamicInletPsiField?.id])
  ));
  const dynamicInletBounds = getNumericValueBounds(selectedDynamicInletValue);

  return {
    fieldIds: {
      dischargePsi: dischargePsiField.id,
      gpm: gpmField.id,
      motorHp: motorHpField.id,
    },
    valuesByFieldId: {
      [dischargePsiField.id]: psiValues,
      [gpmField.id]: gpmValues,
      [motorHpField.id]: hpValues,
    },
    tuples,
    dischargePsiAdjustment: {
      configurationValue: selectedConfigurationValue || null,
      dynamicInletPsiFieldId: dynamicInletPsiField?.id || 0,
      defaultDynamicInletPsi: dynamicInletBounds?.min ?? 0,
    },
    defaults: {
      inputs: {
        [dischargePsiField.id]: minimumForValues(psiValues),
        [gpmField.id]: minimumForValues(gpmValues),
      },
      hpValueId: hpValues[0]?.id || 0,
    },
  };
};

const getNumericValueBounds = (value) => {
  const fixedValue = Number(value?.optionValue);
  const min = value?.minValue !== null && value?.minValue !== undefined
    ? Number(value.minValue)
    : fixedValue;
  const max = value?.maxValue !== null && value?.maxValue !== undefined
    ? Number(value.maxValue)
    : fixedValue;
  return Number.isFinite(min) && Number.isFinite(max) ? { min, max } : null;
};

const buildPumpTableAxisBuckets = (values = []) => {
  const ranges = values
    .map((value) => ({ value, bounds: getNumericValueBounds(value) }))
    .filter(({ bounds }) => bounds && bounds.min <= bounds.max);
  const boundaries = [...new Set(ranges.flatMap(({ bounds }) => [bounds.min, bounds.max + 1]))]
    .sort((left, right) => left - right);

  return boundaries.slice(0, -1).reduce((buckets, min, index) => {
    const max = boundaries[index + 1] - 1;
    const containingValueIds = ranges
      .filter(({ bounds }) => bounds.min <= min && bounds.max >= max)
      .map(({ value }) => value.id);
    if (containingValueIds.length === 0) return buckets;
    buckets.push({
      min,
      max,
      valueIds: containingValueIds,
      label: min === max ? String(min) : `${min}\u2013${max}`,
    });
    return buckets;
  }, []);
};

export const buildPumpTablePreviewModel = (sourceData = {}, stationId, assignmentSelections = null) => {
  const fields = sourceData.fields || [];
  const fieldValues = sourceData.fieldValues || [];
  const valueRules = sourceData.valueRules || [];
  const dischargePsiField = getPerformanceField(fields, PERFORMANCE_FIELD_NAMES.dischargePsi);
  const gpmField = getPerformanceField(fields, PERFORMANCE_FIELD_NAMES.gpm);
  const motorHpField = getPerformanceField(fields, PERFORMANCE_FIELD_NAMES.motorHp);
  if (!dischargePsiField || !gpmField || !motorHpField) {
    return { columns: [], rows: [], pumpTypeTables: [], hasRequiredFields: false, matchedCellCount: 0 };
  }

  const getAxisValues = (field) => {
    const assignedValues = getStationFieldBranches(field, fieldValues, valueRules, stationId)
      .map((branch) => fieldValues.find((value) => value.id === branch.fieldValueId));
    return uniqueValuesById(assignedValues.length > 0
      ? assignedValues
      : fieldValues.filter((value) => value.configFieldId === field.id));
  };
  const fallbackPsiValues = getAxisValues(dischargePsiField);
  const fallbackGpmValues = getAxisValues(gpmField);
  const hpValueById = new Map(fieldValues
    .filter((value) => value.configFieldId === motorHpField.id)
    .map((value) => [value.id, value]));

  const buildMatrix = (constraints, pumpTypeValue = null) => {
    const psiValues = constraints?.valuesByFieldId?.[dischargePsiField.id] || fallbackPsiValues;
    const gpmValues = constraints?.valuesByFieldId?.[gpmField.id] || fallbackGpmValues;
    const columns = buildPumpTableAxisBuckets(gpmValues)
      .map((column, columnIndex) => ({ ...column, columnIndex }));
    const rowBuckets = buildPumpTableAxisBuckets(psiValues);
    let matchedCellCount = 0;
    const rows = rowBuckets.map((row, rowIndex) => ({
      ...row,
      rowIndex,
      cells: columns.map((column) => {
        const matchingTuples = (constraints?.tuples || [])
          .filter((tuple) => (
            row.valueIds.includes(tuple.psiValueId) &&
            column.valueIds.includes(tuple.gpmValueId)
          ));
        const hpValues = uniqueValuesById(matchingTuples
          .map((tuple) => hpValueById.get(tuple.hpValueId)));
        const ruleTargets = [motorHpField, gpmField, dischargePsiField].map((field) => {
          const subSection = (sourceData.subSections || []).find((item) => item.id === field.subSectionId);
          return {
            fieldId: field.id,
            fieldName: field.fieldName,
            subSectionId: field.subSectionId,
            sectionId: subSection?.sectionId,
            branchKeys: [...new Set(matchingTuples.flatMap((tuple) => tuple.ruleBranches || [])
              .filter((branch) => branch.fieldId === field.id).map((branch) => branch.key))],
          };
        }).filter((target) => target.branchKeys.length > 0);
        if (hpValues.length > 0) matchedCellCount += 1;
        return {
          key: `${row.label}:${column.label}`,
          rowIndex,
          columnIndex: column.columnIndex,
          psiLabel: row.label,
          gpmLabel: column.label,
          psiMin: row.min,
          psiMax: row.max,
          gpmMin: column.min,
          gpmMax: column.max,
          psiValueIds: row.valueIds,
          gpmValueIds: column.valueIds,
          hpValueIds: hpValues.map((value) => value.id),
          hpValues: hpValues.map((value) => ({ id: value.id, label: getFieldValueLabel(value) })),
          label: hpValues.map((value) => getFieldValueLabel(value)).join(' / '),
          ruleTargets,
        };
      }),
    }));

    return {
      fieldIds: {
        dischargePsi: dischargePsiField.id,
        gpm: gpmField.id,
        motorHp: motorHpField.id,
      },
      defaultInputs: constraints?.defaults?.inputs || {},
      defaultHpValueId: constraints?.defaults?.hpValueId || 0,
      columns,
      rows,
      hasRequiredFields: true,
      matchedCellCount,
      totalCellCount: rows.length * columns.length,
      pumpTypeValueId: pumpTypeValue?.id || 0,
      pumpTypeLabel: pumpTypeValue ? getFieldValueLabel(pumpTypeValue) : '',
    };
  };

  const pumpTypeField = getPerformanceField(fields, PUMP_TYPE_FIELD_NAME);
  // Assignment matrices must evaluate the chosen context, not union together
  // incompatible pump descriptions/configurations as an overview can.
  if (assignmentSelections !== null) {
    return { ...buildMatrix(buildPreviewPerformanceConstraints(sourceData, stationId,
      assignmentSelections, { includeRuleBranches: true })), pumpTypeFieldId: pumpTypeField?.id || 0, pumpTypeTables: [] };
  }
  const valueById = new Map(fieldValues.map((value) => [value.id, value]));
  const pumpTypeValues = pumpTypeField
    ? uniqueValuesById([
        ...getStationFieldBranches(dischargePsiField, fieldValues, valueRules, stationId),
        ...getStationFieldBranches(gpmField, fieldValues, valueRules, stationId),
        ...getStationFieldBranches(motorHpField, fieldValues, valueRules, stationId),
      ].flatMap((branch) => branch.dependencies)
        .map((dependency) => valueById.get(asNumber(dependency.parentFieldValueId)))
        .filter((value) => value?.configFieldId === pumpTypeField.id))
    : [];

  if (pumpTypeValues.length > 0) {
    const pumpTypeTables = pumpTypeValues.map((pumpTypeValue) => buildMatrix(
      buildPreviewPerformanceConstraints(
        sourceData,
        stationId,
        { [pumpTypeField.id]: pumpTypeValue.id },
        { externalDependencyFieldIds: [pumpTypeField.id], includeRuleBranches: true }
      ),
      pumpTypeValue
    ));
    return {
      ...pumpTypeTables[0],
      pumpTypeFieldId: pumpTypeField.id,
      pumpTypeTables,
    };
  }

  const matrix = buildMatrix(buildPreviewPerformanceConstraints(
    sourceData,
    stationId,
    {},
    { ignoreExternalDependencies: true, includeRuleBranches: true }
  ));
  return { ...matrix, pumpTypeFieldId: 0, pumpTypeTables: [] };
};

const formatAllowedValues = (values = []) => uniqueValuesById(values)
  .map((value) => getFieldValueLabel(value))
  .join(', ');

const formatAllowedNumberRanges = (values = [], offset = 0) => {
  const numericOffset = Number.isFinite(Number(offset)) ? Number(offset) : 0;
  const ranges = uniqueValuesById(values)
    .map((value) => {
      const fixedValue = Number(value.optionValue);
      const min = value.minValue !== null && value.minValue !== undefined
        ? Number(value.minValue)
        : fixedValue;
      const max = value.maxValue !== null && value.maxValue !== undefined
        ? Number(value.maxValue)
        : fixedValue;
      return Number.isFinite(min) && Number.isFinite(max)
        ? { min: min + numericOffset, max: max + numericOffset }
        : null;
    })
    .filter(Boolean)
    .sort((left, right) => left.min - right.min || left.max - right.max);

  const merged = ranges.reduce((result, range) => {
    const previous = result[result.length - 1];
    if (previous && range.min <= previous.max + 1) {
      previous.max = Math.max(previous.max, range.max);
    } else {
      result.push({ ...range });
    }
    return result;
  }, []);

  return merged.map(({ min, max }) => (min === max ? String(min) : `${min}–${max}`)).join(', ');
};

export const validatePreviewPerformance = (constraints, selections = {}, inputs = {}) => {
  if (!constraints) return null;
  const { dischargePsi, gpm, motorHp } = constraints.fieldIds;
  const psiInput = inputs[dischargePsi] ?? constraints.defaults.inputs[dischargePsi];
  const gpmInput = inputs[gpm] ?? constraints.defaults.inputs[gpm];
  const hpValueId = asNumber(selections[motorHp]) || constraints.defaults.hpValueId;
  const adjustment = constraints.dischargePsiAdjustment || {};
  const dynamicInletPsiInput = inputs[adjustment.dynamicInletPsiFieldId]
    ?? adjustment.defaultDynamicInletPsi
    ?? 0;
  const adjustedPsiInput = getAdjustedDischargePsi({
    configurationValue: adjustment.configurationValue,
    dischargePsi: psiInput,
    dynamicInletPsi: dynamicInletPsiInput,
  });
  const isBoosterAdjustment = Number(adjustedPsiInput) !== Number(psiInput);
  const dischargePsiHintOffset = isBoosterAdjustment ? Number(dynamicInletPsiInput) || 0 : 0;
  const valuesById = new Map(Object.values(constraints.valuesByFieldId)
    .flat()
    .map((value) => [value.id, value]));
  const tupleMatches = (tuple, matchPsi = true, matchGpm = true, matchHp = true) => (
    (!matchPsi || valueContainsNumber(valuesById.get(tuple.psiValueId), adjustedPsiInput)) &&
    (!matchGpm || valueContainsNumber(valuesById.get(tuple.gpmValueId), gpmInput)) &&
    (!matchHp || tuple.hpValueId === hpValueId)
  );
  const isValid = constraints.tuples.some((tuple) => tupleMatches(tuple));
  const allowedPsiValues = constraints.tuples
    .filter((tuple) => tupleMatches(tuple, false, true, true))
    .map((tuple) => valuesById.get(tuple.psiValueId));
  const allowedGpmValues = constraints.tuples
    .filter((tuple) => tupleMatches(tuple, true, false, true))
    .map((tuple) => valuesById.get(tuple.gpmValueId));
  const allowedHpValues = constraints.tuples
    .filter((tuple) => tupleMatches(tuple, true, true, false))
    .map((tuple) => valuesById.get(tuple.hpValueId));
  const hpCompatibleTuples = constraints.tuples.filter((tuple) => tuple.hpValueId === hpValueId);
  const effectiveAllowedPsiValues = allowedPsiValues.length > 0
    ? allowedPsiValues
    : hpCompatibleTuples.map((tuple) => valuesById.get(tuple.psiValueId));
  const effectiveAllowedGpmValues = allowedGpmValues.length > 0
    ? allowedGpmValues
    : hpCompatibleTuples.map((tuple) => valuesById.get(tuple.gpmValueId));
  const fieldErrors = {};
  const psiNumber = Number(psiInput);
  const gpmNumber = Number(gpmInput);
  const hasPsiInput = psiInput !== '' && psiInput !== null && psiInput !== undefined && Number.isFinite(psiNumber);
  const hasGpmInput = gpmInput !== '' && gpmInput !== null && gpmInput !== undefined && Number.isFinite(gpmNumber);
  if (!hasPsiInput) fieldErrors[dischargePsi] = 'Enter a Discharge PSI value.';
  else if (!effectiveAllowedPsiValues.some((value) => valueContainsNumber(value, adjustedPsiInput))) {
    fieldErrors[dischargePsi] = `Discharge PSI must match ${formatAllowedNumberRanges(effectiveAllowedPsiValues, dischargePsiHintOffset) || 'an available range'} for the current GPM and HP.`;
  }
  if (!hasGpmInput) fieldErrors[gpm] = 'Enter a GPM value.';
  else if (!effectiveAllowedGpmValues.some((value) => valueContainsNumber(value, gpmNumber))) {
    fieldErrors[gpm] = `GPM must match ${formatAllowedValues(effectiveAllowedGpmValues) || 'an available range'} for the current PSI and HP.`;
  }
  if (!allowedHpValues.some((value) => value?.id === hpValueId)) {
    fieldErrors[motorHp] = `Motor HP must be ${formatAllowedValues(allowedHpValues) || 'an available value'} for the current PSI and GPM.`;
  }

  return {
    fieldIds: constraints.fieldIds,
    isValid,
    inputValues: { [dischargePsi]: psiInput, [gpm]: gpmInput },
    adjustedDischargePsi: adjustedPsiInput,
    dynamicInletPsi: dynamicInletPsiInput,
    selectedHpValueId: hpValueId,
    fieldErrors,
    fieldHints: {
      [dischargePsi]: `Must be: ${formatAllowedNumberRanges(effectiveAllowedPsiValues, dischargePsiHintOffset) || 'No match'}`,
      [gpm]: `Must be: ${formatAllowedNumberRanges(effectiveAllowedGpmValues) || 'No match'}`,
      [motorHp]: '',
    },
  };
};

export const buildConfigurationPreviewModel = (sourceData = {}, stationId, sectionId = null, selections = {}, inputs = {}) => {
  const data = {
    sections: sourceData.sections || [],
    subSections: sourceData.subSections || [],
    fields: sourceData.fields || [],
    fieldValues: sourceData.fieldValues || [],
    valueRules: sourceData.valueRules || [],
  };
  const selectedSectionId = asNumber(sectionId);
  const allStationSections = getSectionsForStation(data.sections, stationId);
  const sections = allStationSections
    .filter((section) => selectedSectionId <= 0 || section.id === selectedSectionId);
  const allStationSectionIds = new Set(allStationSections.map((section) => section.id));
  const sectionIds = new Set(sections.map((section) => section.id));
  const allStationSubSections = sortByOrderThenName(
    data.subSections.filter((subSection) => allStationSectionIds.has(subSection.sectionId)),
    'subSectionName'
  );
  const subSections = allStationSubSections.filter((subSection) => sectionIds.has(subSection.sectionId));
  const allStationSubSectionIds = new Set(allStationSubSections.map((subSection) => subSection.id));
  const subSectionIds = new Set(subSections.map((subSection) => subSection.id));
  const allStationFields = sortByOrderThenName(
    data.fields.filter((field) => allStationSubSectionIds.has(field.subSectionId)),
    'fieldName'
  );
  const fields = allStationFields.filter((field) => subSectionIds.has(field.subSectionId));
  const performanceCandidateFieldIds = new Set(Object.values(PERFORMANCE_FIELD_NAMES)
    .map((fieldName) => getPerformanceField(allStationFields, fieldName)?.id)
    .filter(Boolean));
  let effectiveSelections = Object.entries(selections || {}).reduce((accumulator, [fieldId, valueId]) => {
    const numericFieldId = asNumber(fieldId);
    const numericValueId = asNumber(valueId);
    if (numericFieldId > 0 && numericValueId > 0) accumulator[numericFieldId] = numericValueId;
    return accumulator;
  }, {});

  for (let pass = 0; pass < Math.max(allStationFields.length + 1, 2); pass += 1) {
    let changed = false;
    for (const field of allStationFields.filter((item) => !performanceCandidateFieldIds.has(item.id))) {
      const allowedValues = getPreviewAllowedFieldValues({
        field,
        fieldValues: data.fieldValues,
        valueRules: data.valueRules,
        stationId,
        selections: effectiveSelections,
      });
      const currentValueId = asNumber(effectiveSelections[field.id]);
      const nextValueId = allowedValues.length > 0
        ? (allowedValues.some((value) => value.id === currentValueId)
          ? currentValueId
          : getPreferredPreviewValueId(allowedValues))
        : 0;
      if (nextValueId !== currentValueId) {
        if (nextValueId > 0) effectiveSelections[field.id] = nextValueId;
        else delete effectiveSelections[field.id];
        changed = true;
      }
    }
    if (!changed) break;
  }

  const performanceConstraints = buildPreviewPerformanceConstraints(data, stationId, effectiveSelections);
  const performanceFieldIds = new Set(Object.values(performanceConstraints?.fieldIds || {}));
  const getAllowedValuesForField = (field, currentSelections) => {
    if (performanceFieldIds.has(field.id)) {
      return (performanceConstraints.valuesByFieldId[field.id] || []).map((value) => ({
        ...value,
        label: getFieldValueLabel(value),
        isDisabled: false,
        isRuleDefault: false,
      }));
    }
    return getPreviewAllowedFieldValues({
      field,
      fieldValues: data.fieldValues,
      valueRules: data.valueRules,
      stationId,
      selections: currentSelections,
    });
  };

  for (let pass = 0; pass < Math.max(allStationFields.length + 1, 2); pass += 1) {
    let changed = false;
    for (const field of allStationFields) {
      const allowedValues = getAllowedValuesForField(field, effectiveSelections);
      const currentValueId = asNumber(effectiveSelections[field.id]);
      if (allowedValues.length === 0) {
        if (currentValueId > 0) {
          delete effectiveSelections[field.id];
          changed = true;
        }
        continue;
      }

      const hasCurrentValue = allowedValues.some((value) => value.id === currentValueId);
      if (!hasCurrentValue) {
        const nextValueId = getPreferredPreviewValueId(allowedValues);
        if (nextValueId !== currentValueId) {
          effectiveSelections = { ...effectiveSelections, [field.id]: nextValueId };
          changed = true;
        }
      }
    }
    if (!changed) break;
  }

  const performanceValidation = validatePreviewPerformance(performanceConstraints, effectiveSelections, inputs);
  const effectiveInputs = Object.entries(inputs || {}).reduce((accumulator, [fieldId, value]) => {
    const numericFieldId = asNumber(fieldId);
    if (
      numericFieldId > 0
      && asNumber(effectiveSelections[numericFieldId]) > 0
      && value !== ''
      && value !== null
      && value !== undefined
      && Number.isFinite(Number(value))
    ) {
      accumulator[numericFieldId] = Number(value);
    }
    return accumulator;
  }, {});
  Object.entries(effectiveSelections).forEach(([fieldId, valueId]) => {
    const numericFieldId = asNumber(fieldId);
    const field = allStationFields.find((item) => item.id === numericFieldId);
    if (!isNumericFieldType(field?.fieldType)) return;
    const performanceInput = performanceValidation?.inputValues?.[numericFieldId];
    if (performanceInput !== undefined) {
      effectiveInputs[numericFieldId] = Number(performanceInput);
      return;
    }
    if (effectiveInputs[numericFieldId] !== undefined) return;
    const selectedValue = data.fieldValues.find((value) => value.id === asNumber(valueId));
    const bounds = getNumericValueBounds(selectedValue);
    if (bounds) effectiveInputs[numericFieldId] = bounds.min;
  });
  const selectedValueIds = getSelectedValueIdSet(effectiveSelections);
  const previewSections = sections.map((section) => {
    const sectionSubSections = subSections
      .filter((subSection) => subSection.sectionId === section.id)
      .map((subSection) => {
        let hiddenFieldCount = 0;
        const disabledFields = [];
        const previewFields = fields
          .filter((field) => field.subSectionId === subSection.id)
          .map((field) => {
            const allowedValues = getAllowedValuesForField(field, effectiveSelections);
            if (allowedValues.length === 0) {
              hiddenFieldCount += 1;
              return null;
            }
            const selectedValueId = asNumber(effectiveSelections[field.id]) || getPreferredPreviewValueId(allowedValues);
            const performanceInput = performanceValidation?.inputValues?.[field.id];
            const selectedValue = performanceFieldIds.has(field.id) && performanceInput !== undefined
              ? allowedValues.find((value) => valueContainsNumber(value, performanceInput)) || allowedValues[0]
              : allowedValues.find((value) => value.id === selectedValueId) || allowedValues[0];
            const normalizedType = asText(field.fieldType).toLowerCase();
            const disabledByRules = allowedValues.every((value) => value.isDisabled);
            const previewField = {
              ...field,
              values: allowedValues,
              selectedValueId,
              selectedValue,
              selectedValueIds,
              inputValue: effectiveInputs[field.id],
              isDisabled: normalizedType === 'calculated',
              isSelectedValueDisabled: Boolean(selectedValue?.isDisabled),
              isHidden: disabledByRules,
              performance: performanceFieldIds.has(field.id) ? {
                inputValue: performanceInput,
                error: performanceValidation?.fieldErrors?.[field.id] || '',
                hint: performanceValidation?.fieldHints?.[field.id] || '',
              } : null,
            };
            if (disabledByRules) {
              hiddenFieldCount += 1;
              disabledFields.push(previewField);
              return null;
            }
            return previewField;
          })
          .filter(Boolean);

        return {
          ...subSection,
          fields: previewFields,
          disabledFields,
          hiddenFieldCount,
        };
      });

    return {
      ...section,
      subSections: sectionSubSections,
      hiddenFieldCount: sectionSubSections.reduce((total, subSection) => total + subSection.hiddenFieldCount, 0),
    };
  });

  return {
    stationId: asNumber(stationId),
    sectionId: selectedSectionId || null,
    sections: previewSections,
    effectiveSelections,
    effectiveInputs,
    performance: performanceValidation,
    hiddenFieldCount: previewSections.reduce((total, section) => total + section.hiddenFieldCount, 0),
    fieldCount: previewSections.reduce((sectionTotal, section) => (
      sectionTotal + section.subSections.reduce((subSectionTotal, subSection) => subSectionTotal + subSection.fields.length, 0)
    ), 0),
  };
};

export const copyValueRuleGroupDraft = (group, stationId, rules) => ({
  ruleGroupId: getNextRuleGroupId(rules),
  parentStationId: stationId,
  valueBranches: group.values.map((branch, index) => ({
    clientKey: `copy-value-${index}`,
    fieldValueId: branch.fieldValueId,
    stationRuleId: null,
    dependencies: [
      ...branch.dependencies.map((dependency) => dependency.parentFieldValueId),
      ...(branch.stationRule?.parentFieldValueId ? [branch.stationRule.parentFieldValueId] : []),
    ].map((parentFieldValueId, dependencyIndex) => ({
      clientKey: `copy-dependency-${index}-${dependencyIndex}`,
      parentFieldValueId,
    })),
    isDisabled: branch.isDisabled,
    isDefaultValue: false,
  })),
});

export const buildValueRuleGroupRows = (draft = {}) => {
  return (draft.valueBranches || []).flatMap((branch) => {
    const shared = {
      fieldValueId: branch.fieldValueId,
      ruleGroupId: draft.ruleGroupId,
      isDisabled: Boolean(branch.isDisabled),
      isDefaultValue: Boolean(branch.isDefaultValue),
    };
    return [
      {
        ...shared,
        id: branch.stationRuleId,
        parentStationId: draft.parentStationId,
        parentFieldValueId: null,
      },
      ...(branch.dependencies || []).map((dependency) => ({
        ...shared,
        id: dependency.id,
        parentStationId: null,
        parentFieldValueId: dependency.parentFieldValueId,
      })),
    ];
  });
};

export const buildValueRuleGroupOperations = (draft = {}, existingRows = []) => {
  const desiredRows = buildValueRuleGroupRows(draft);
  const desiredIds = new Set(desiredRows.map((row) => asNumber(row.id)).filter((id) => id > 0));
  return [
    ...desiredRows.map((row) => ({
      method: asNumber(row.id) > 0 ? 'PUT' : 'POST',
      payload: buildPayload('valueRules', row, asNumber(row.id) > 0 ? 'edit' : 'create'),
    })),
    ...(existingRows || [])
      .filter((row) => asNumber(row.id) > 0 && !desiredIds.has(asNumber(row.id)))
      .map((row) => ({ method: 'DELETE', payload: { Id: asNumber(row.id) } })),
  ];
};

export const validateValueRuleGroupDraft = (draft = {}) => {
  if (asNumber(draft.ruleGroupId) <= 0) return 'Rule group must be a positive number.';
  if (asNumber(draft.parentStationId) <= 0) return 'The station condition is required.';
  const branches = draft.valueBranches || [];
  if (branches.length === 0) return 'Add at least one allowed value to the rule group.';
  if (branches.some((branch) => asNumber(branch.fieldValueId) <= 0)) return 'Choose an allowed field value for every value branch.';
  const fieldValueIds = branches.map((branch) => asNumber(branch.fieldValueId));
  if (new Set(fieldValueIds).size !== fieldValueIds.length) return 'Each allowed value can appear only once in a rule group.';
  if (branches.filter((branch) => branch.isDefaultValue).length > 1) return 'Only one allowed value can be the default in a rule group.';

  for (const branch of branches) {
    const dependencies = branch.dependencies || [];
    if (dependencies.some((dependency) => asNumber(dependency.parentFieldValueId) <= 0)) {
      return 'Choose a field value for every AND condition.';
    }
    const dependencyIds = dependencies.map((dependency) => asNumber(dependency.parentFieldValueId));
    if (new Set(dependencyIds).size !== dependencyIds.length) return 'Each AND condition for an allowed value must use a different field value.';
  }
  return '';
};

export const findDeleteBlocker = (resource, record, data) => {
  if (!record) return '';

  if (resource === 'stations') {
    const sectionCount = data.sections.filter((section) => section.stationId === record.id).length;
    const ruleCount = data.valueRules.filter((rule) => rule.parentStationId === record.id).length;
    if (sectionCount || ruleCount) return `Remove its ${sectionCount} station-specific section(s) and ${ruleCount} value rule(s) first.`;
  }

  if (resource === 'sections') {
    const count = data.subSections.filter((subSection) => subSection.sectionId === record.id).length;
    if (count) return `Remove its ${count} subsection(s) first.`;
  }

  if (resource === 'subSections') {
    const count = data.fields.filter((field) => field.subSectionId === record.id).length;
    if (count) return `Remove its ${count} field(s) first.`;
  }

  if (resource === 'fields') {
    const count = data.fieldValues.filter((value) => value.configFieldId === record.id).length;
    if (count) return `Remove its ${count} possible value(s) first.`;
  }

  if (resource === 'fieldValues') {
    const count = data.valueRules.filter(
      (rule) => rule.fieldValueId === record.id || rule.parentFieldValueId === record.id
    ).length;
    if (count) return `Remove the ${count} rule(s) that reference this value first.`;
  }

  return '';
};

export const buildPayload = (resource, draft, mode) => {
  const includeId = mode === 'edit' || resource === 'fields';
  const id = asNumber(draft.id);

  const payloads = {
    stations: {
      StationName: asText(draft.stationName),
      SortOrder: asNumber(draft.sortOrder),
    },
    sections: {
      StationId: asNullableNumber(draft.stationId),
      Page: asText(draft.page),
      SectionKey: asText(draft.sectionKey),
      Label: asText(draft.label) || null,
      SortOrder: asNumber(draft.sortOrder),
    },
    subSections: {
      SectionId: asNumber(draft.sectionId),
      SubSectionName: asText(draft.subSectionName),
      SortOrder: asNumber(draft.sortOrder),
    },
    fields: {
      SubSectionId: asNumber(draft.subSectionId),
      FieldName: asText(draft.fieldName),
      FieldType: asText(draft.fieldType),
      Scope: asText(draft.scope),
      SortOrder: asNumber(draft.sortOrder),
    },
    fieldValues: {
      ConfigFieldId: asNumber(draft.configFieldId),
      OptionValue: isNumericFieldType(draft.fieldType) && draft.valueMode === 'range'
        ? `${asNumber(draft.minValue)}-${asNumber(draft.maxValue)}`
        : asText(draft.optionValue),
      IsDefaultValue: isNumericFieldType(draft.fieldType) && draft.valueMode === 'range'
        ? false
        : Boolean(draft.isDefaultValue),
      MinValue: isNumericFieldType(draft.fieldType) && draft.valueMode === 'range'
        ? asNullableNumber(draft.minValue)
        : null,
      MaxValue: isNumericFieldType(draft.fieldType) && draft.valueMode === 'range'
        ? asNullableNumber(draft.maxValue)
        : null,
      MaxRows: isButtonFieldType(draft.fieldType)
        ? asNullableNumber(draft.maxClicks)
        : null,
    },
    valueRules: {
      FieldValueId: asNumber(draft.fieldValueId),
      RuleGroupId: asNumber(draft.ruleGroupId),
      ParentStationId: asNullableNumber(draft.parentStationId),
      ParentFieldValueId: asNullableNumber(draft.parentFieldValueId),
      IsDisabled: Boolean(draft.isDisabled),
      IsDefaultValue: Boolean(draft.isDefaultValue),
    },
  };

  return includeId ? { Id: id, ...payloads[resource] } : payloads[resource];
};

export const validateDraft = (resource, draft) => {
  if (resource === 'stations' && !asText(draft.stationName)) return 'Station name is required.';
  if (resource === 'sections' && (!asText(draft.page) || !asText(draft.sectionKey))) return 'Page and section name are required.';
  if (resource === 'subSections' && !asText(draft.subSectionName)) return 'Subsection name is required.';
  if (resource === 'fields') {
    if (asNumber(draft.id) <= 0) return 'A positive field ID is required by the API.';
    if (!asText(draft.fieldName) || !asText(draft.fieldType) || !asText(draft.scope)) return 'Field name, type, and scope are required.';
  }
  if (resource === 'fieldValues') {
    if (isNumericFieldType(draft.fieldType)) {
      const requiresInteger = isWholeNumberFieldType(draft.fieldType);
      if (draft.valueMode === 'range') {
        const minValue = asNullableNumber(draft.minValue);
        const maxValue = asNullableNumber(draft.maxValue);
        if (minValue === null || maxValue === null) return 'Both minimum and maximum values are required.';
        if (requiresInteger && (!Number.isInteger(minValue) || !Number.isInteger(maxValue))) return 'Minimum and maximum must be whole numbers.';
        if (minValue > maxValue) return 'Minimum value cannot be greater than maximum value.';
      } else {
        const fixedValue = asNullableNumber(draft.optionValue);
        if (fixedValue === null) return 'A fixed value is required.';
        if (requiresInteger && !Number.isInteger(fixedValue)) return 'Fixed value must be a whole number.';
      }
    } else if (isHyperlinkFieldType(draft.fieldType)) {
      if (!asText(draft.optionValue)) return 'Link URL is required.';
      if (!normalizeHyperlinkHref(draft.optionValue)) return 'Enter a valid http, https, mailto, tel, or Sales Portal path link.';
    } else if (isButtonFieldType(draft.fieldType)) {
      const maxClicks = asNullableNumber(draft.maxClicks);
      if (maxClicks === null) return 'Maximum rows is required.';
      if (!Number.isInteger(maxClicks) || maxClicks <= 0) return 'Maximum rows must be a positive whole number.';
    } else if (!asText(draft.optionValue)) {
      return 'Option value is required.';
    }
  }
  if (resource === 'valueRules') {
    if (asNumber(draft.fieldValueId) <= 0) return 'Field value is required.';
    if (asNumber(draft.ruleGroupId) <= 0) return 'Rule group must be a positive number.';
    const hasStationParent = asNullableNumber(draft.parentStationId) !== null;
    const hasFieldValueParent = asNullableNumber(draft.parentFieldValueId) !== null;
    if (hasStationParent === hasFieldValueParent) return 'Each rule row must have exactly one parent: station or field value.';
  }
  return '';
};
